import { Check, ShieldAlert, X } from 'lucide-react';
import { useState } from 'react';
import { DESTRUCTIVE_RISK_LEVEL, type AdminAction } from '../../../../services/adminAgent.service';
import { ActionJsonBlock } from './ActionJsonBlock';
import { ACTION_STATUS_STYLES } from './actionStatusStyles';

const CONFIRM_WORD = 'DELETE';

interface ActionApprovalCardProps {
  action: AdminAction;
  busy: boolean;
  onApprove: (publicId: string, confirmed: boolean) => void;
  onReject: (publicId: string) => void;
}

export const ActionApprovalCard: React.FC<ActionApprovalCardProps> = ({
  action,
  busy,
  onApprove,
  onReject,
}) => {
  const [typed, setTyped] = useState('');
  const isPending = action.status === 'pending';
  const isDestructive = action.riskLevel === DESTRUCTIVE_RISK_LEVEL;
  const canApprove = !busy && (!isDestructive || typed.trim().toUpperCase() === CONFIRM_WORD);

  return (
    <div className="min-w-0 space-y-2 overflow-hidden rounded-lg border border-slate-200 bg-white p-3 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 font-semibold text-slate-900 dark:text-slate-100">
            {isDestructive && <ShieldAlert size={14} className="shrink-0 text-red-500" />}
            <span className="truncate">{action.operationId}</span>
          </div>
          <div className="break-all font-mono text-xs text-slate-500 dark:text-slate-400">
            {action.method} {action.path}
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${ACTION_STATUS_STYLES[action.status]}`}
        >
          {action.status}
        </span>
      </div>

      {action.reason && <p className="text-slate-700 dark:text-slate-300">{action.reason}</p>}
      <ActionJsonBlock label="Current data" value={action.preview} />
      <ActionJsonBlock label="Query" value={action.query} />
      <ActionJsonBlock label="Body" value={action.body} />
      {action.resultSummary && (
        <p className="max-h-32 overflow-y-auto whitespace-pre-wrap break-all text-xs text-slate-600 [overflow-wrap:anywhere] dark:text-slate-400">
          {action.resultSummary}
        </p>
      )}

      {isPending && (
        <div className="space-y-2 pt-1">
          {isDestructive && (
            <input
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={`Type ${CONFIRM_WORD} to confirm`}
              className="w-full rounded-md border border-red-300 bg-transparent px-2 py-1 text-xs text-slate-900 outline-none focus:border-red-500 dark:border-red-800 dark:text-slate-100"
            />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!canApprove}
              onClick={() => onApprove(action.publicId, isDestructive)}
              className="flex flex-1 items-center justify-center gap-1 rounded-md bg-blue-500 px-3 py-1.5 text-white transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Check size={14} /> Approve
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onReject(action.publicId)}
              className="flex flex-1 items-center justify-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <X size={14} /> Reject
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
